import React, { useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import './userPanelCommentsModal.css';

const maskUsername = (value) => {
  const username = String(value || '—');
  if (username.length <= 2) return username;
  return `${username.charAt(0)}${'*'.repeat(Math.min(4, username.length - 2))}${username.charAt(username.length - 1)}`;
};

const formatCommentDate = (value, fallback) => {
  if (fallback) return fallback;
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const normalizeComment = (comment, index) => ({
  id: comment.id || `comment-${index}`,
  username: maskUsername(comment.kullanici_adi || comment.username),
  dateLabel: formatCommentDate(comment.olusturma_tarihi || comment.createdAt, comment.tarih_label),
  rating: Math.max(0, Math.min(5, Number(comment.rating) || 0)),
  text: String(comment.yorum || comment.text || ''),
  helpfulCount: Math.max(0, Number(comment.helpful_count ?? comment.helpfulCount) || 0),
});

function UserPanelCommentsModal({ open, onClose, content, contentType, onOpenReview, onToggleHelpful }) {
  const [helpfulLoadingId, setHelpfulLoadingId] = useState(null);
  const [helpfulError, setHelpfulError] = useState('');
  const isSetup = contentType === 'setup';
  const contentName = String(content?.name || content?.title || (isSetup ? 'Uygulama' : 'İçerik'));
  const poster = isSetup
    ? content?.logo || content?.poster || content?.cover || content?.stream_icon || ''
    : content?.poster || content?.cover || content?.stream_icon || '';
  const score = content?.rating || content?.review?.rating || content?.imdbRating || content?.imdb_rating || '—';
  const contentLabel = isSetup ? 'Uygulama' : contentType === 'series' ? 'Dizi' : 'Film';
  const scoreDenominator = isSetup ? 5 : 10;
  const comments = useMemo(() => {
    const source = Array.isArray(content?.comments) ? content.comments : [];
    return source.map(normalizeComment);
  }, [content]);

  const toggleHelpful = async (commentId) => {
    if (helpfulLoadingId !== null) return;
    setHelpfulLoadingId(commentId);
    setHelpfulError('');
    try {
      if (typeof onToggleHelpful !== 'function') throw new Error('Faydalı yorum bağlantısı kullanılamıyor.');
      await onToggleHelpful(commentId);
    } catch (error) {
      setHelpfulError(error?.message || 'Faydalı işlemi tamamlanamadı.');
    } finally {
      setHelpfulLoadingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-comments-dialog"
      aria-labelledby="tvup-comments-modal-title"
      slotProps={{
        backdrop: { className: 'tvup-comments-backdrop' },
        paper: { id: 'tvup-comments-modal', className: 'tvup-comments-modal' },
      }}
    >
      <header className="tvup-comments-header">
        <span className="tvup-comments-title-icon" aria-hidden="true"><ChatBubbleOutlineRoundedIcon /></span>
        <div className="tvup-comments-title-copy">
          <h2 id="tvup-comments-modal-title">Yorumlar</h2>
          <p><span>{contentName}</span> · {contentLabel}</p>
        </div>
        <button type="button" className="tvup-comments-close" aria-label="Yorumları kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <div className="tvup-comments-content">
        <section className="tvup-comments-summary" aria-label={`${contentName} değerlendirme özeti`}>
          <span className={`tvup-comments-poster${isSetup ? ' is-setup' : ''}`} aria-hidden="true">
            {poster ? <img src={poster} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <b>{contentName.charAt(0)}</b>}
          </span>
          <div className="tvup-comments-summary-copy">
            <small>{isSetup ? 'TIVIGO UYGULAMA DEĞERLENDİRMELERİ' : 'TIVIGO KULLANICI DEĞERLENDİRMELERİ'}</small>
            <h3>{contentName}</h3>
            <p>
              {isSetup
                ? 'Kullanıcıların bu uygulamanın kurulumu ve kullanımı hakkındaki puanlarını ve yorumlarını inceleyin.'
                : 'İzleyenlerin bu içerik hakkındaki puanlarını ve yorumlarını inceleyin.'}
            </p>
          </div>
          <div className="tvup-comments-score" aria-label={`${score} puan, ${scoreDenominator} üzerinden`}>
            <StarRoundedIcon aria-hidden="true" />
            <strong>{score}</strong>
            <small>{scoreDenominator} üzerinden</small>
          </div>
        </section>

        <div className="tvup-comments-list-heading">
          <div>
            <h3>Tivigo abonelerinin yorumları</h3>
            <p>Topluluğun son değerlendirmeleri</p>
          </div>
          <button
            type="button"
            className="tvup-comments-review-button"
            aria-haspopup="dialog"
            aria-controls="tvup-review-modal"
            onClick={onOpenReview}
          >
            <RateReviewRoundedIcon aria-hidden="true" />
            Sen de Yorum Yap
          </button>
        </div>

        <section className="tvup-comments-list" aria-label={`${contentName} yorumları`}>
          {helpfulError ? <p role="alert">{helpfulError}</p> : null}
          {content?.commentsLoading ? (
            <p role="status">Yorumlar yükleniyor...</p>
          ) : content?.commentsError ? (
            <p role="alert">{content.commentsError}</p>
          ) : comments.length === 0 ? (
            <div className="tvup-comments-empty" role="status">
              <span className="tvup-comments-empty-icon" aria-hidden="true">
                <ChatBubbleOutlineRoundedIcon />
              </span>
              <div className="tvup-comments-empty-copy">
                <strong>Henüz yorum yok</strong>
                <p>Bu içerik için henüz yayınlanmış yorum bulunmuyor.</p>
              </div>
            </div>
          ) : comments.map((comment) => (
            <article key={comment.id} className="tvup-comment-card">
              <span className="tvup-comment-avatar" aria-hidden="true">{comment.username.charAt(0).toLocaleUpperCase('tr-TR')}</span>
              <div className="tvup-comment-body">
                <header>
                  <div>
                    <b>{comment.username}</b>
                    <small>{comment.dateLabel}</small>
                  </div>
                  {comment.rating > 0 && (
                    <span className="tvup-comment-stars" aria-label={`${comment.rating} üzerinden 5 puan`}>
                      {Array.from({ length: 5 }, (_, index) => (
                        <StarRoundedIcon key={index} className={index < Math.round(comment.rating) ? 'is-filled' : ''} />
                      ))}
                    </span>
                  )}
                </header>
                <p>{comment.text}</p>
                <footer>
                  <button type="button" disabled={helpfulLoadingId !== null} onClick={() => toggleHelpful(comment.id)}>
                    <ThumbUpOutlinedIcon aria-hidden="true" />
                    <span>{helpfulLoadingId === comment.id ? 'Güncelleniyor...' : `${comment.helpfulCount} kişi faydalı buldu`}</span>
                  </button>
                </footer>
              </div>
            </article>
          ))}
        </section>
      </div>
    </Dialog>
  );
}

export default UserPanelCommentsModal;
